// DiagnosticFlow - Property questions with CoStar-style dark UI
// Features: Progress persistence, tooltips, auto-advance, smooth transitions, haptic feedback
// V0.9.2: Removed micro-moment feedback

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Check, HelpCircle, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/components/ThemeProvider";
import { feedback } from "@/utils/feedback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero image for background
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/jiw8ixlc_edensdad._cinematic_close-up_cross-view_of_luxury_British_home__aa4984b8-4df0-4c41-b9bc-f7261784d14d.png";

// Questions with tooltips - Property Era is FIRST
const ALL_QUESTIONS = [
  { 
    id: "property_era", 
    title: "Property Era", 
    desc: "When was this property built?", 
    tooltip: "Pre-2010 homes often have more conversion potential. New builds may have restrictions.",
    opts: [["classic","Classic"],["newbuild","New Build"]],
    helperText: {
      "classic": "Homes built earlier, often with layouts that differ from modern standards",
      "newbuild": "Homes built around or after 2010, typically designed to modern standards"
    }
  },
  { 
    id: "dwelling_type", 
    title: "Property Style", 
    desc: "What style is the property?", 
    tooltip: "Different property types have varying potential for internal improvements and conversions.",
    opts: [["detached","Detached House"],["semi-detached","Semi-Detached"],["terrace","Terraced House"],["bungalow","Bungalow"],["flat","Flat / Apartment"]] 
  },
  { 
    id: "bedrooms", 
    title: "Bedrooms", 
    desc: "How many bedrooms?", 
    tooltip: "Bedroom count affects property value bands and market comparisons.",
    opts: [["1","1 bedroom"],["2","2 bedrooms"],["3","3 bedrooms"],["4","4+ bedrooms"]] 
  },
  { 
    id: "bathrooms", 
    title: "Bathrooms", 
    desc: "How many bathrooms?", 
    tooltip: "The bathroom-to-bedroom ratio impacts spatial efficiency scoring.",
    opts: [["1","1 bathroom"],["2","2 bathrooms"],["3","3+ bathrooms"]] 
  },
  { 
    id: "separate_kitchen", 
    title: "Kitchen Layout", 
    desc: "How is your kitchen arranged?", 
    tooltip: "Open-plan layouts are popular but separate kitchens offer conversion potential.",
    opts: [["true","Separate enclosed kitchen"],["partial","Partially open (archway, breakfast bar)"],["false","Open-plan kitchen/living"]] 
  },
  { 
    id: "layout_changed", 
    title: "Any Changes Made?", 
    desc: "Has any internal layout work been done?", 
    tooltip: "Previous modifications suggest the property is adaptable.",
    opts: [["true","Yes — walls moved, rooms changed"],["false","No — original layout"],["unsure","Not sure"]] 
  },
  { 
    id: "features", 
    title: "Property Features", 
    desc: "Select all that apply", 
    tooltip: "These features can significantly impact unlockability and value potential.",
    opts: [["garage","Garage"],["loft","Loft Space (accessible or boarded)"],["conservatory","Conservatory"],["garden","Garden"],["none","None of these"]], 
    multi: true, 
    skipForFlat: true 
  },
  { 
    id: "epc_rating", 
    title: "EPC Rating", 
    desc: "Energy Performance Certificate band?", 
    tooltip: "EPC affects both value and improvement potential. Check your rating at gov.uk.",
    opts: [["A-B","A or B (Most efficient)"],["C","C (Average)"],["D-E","D or E"],["F-G","F or G (Least efficient)"],["unknown","Not sure — that's OK"]] 
  },
  { 
    id: "main_goal", 
    title: "Your Goal", 
    desc: "What's your main objective?", 
    tooltip: "This helps us prioritise the insights most relevant to you.",
    opts: [["maximise_value","Maximise my home's value"],["rent","Optimise for rental"],["explore","Just exploring"]] 
  }
];

export default function DiagnosticFlow() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  // Initialize state from sessionStorage if available
  const getInitialState = () => {
    if (typeof window === 'undefined') return { step: 0, answers: getDefaultAnswers() };
    
    const savedProgress = sessionStorage.getItem(`cipher_diagnostic_progress_${sessionId}`);
    if (savedProgress) {
      try {
        const { savedStep, savedAnswers } = JSON.parse(savedProgress);
        return {
          step: savedStep || 0,
          answers: savedAnswers || getDefaultAnswers()
        };
      } catch (e) {
        return { step: 0, answers: getDefaultAnswers() };
      }
    }
    return { step: 0, answers: getDefaultAnswers() };
  };
  
  const getDefaultAnswers = () => ({
    property_era: "",
    dwelling_type: "",
    bedrooms: "",
    bathrooms: "",
    separate_kitchen: "",
    layout_changed: "",
    features: [],
    epc_rating: "",
    main_goal: ""
  });
  
  const initialState = getInitialState();
  const [step, setStep] = useState(initialState.step);
  const [loading, setLoading] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [answers, setAnswers] = useState(initialState.answers);
  const [exitTransition, setExitTransition] = useState(false);

  // Save progress on changes
  useEffect(() => {
    sessionStorage.setItem(`cipher_diagnostic_progress_${sessionId}`, JSON.stringify({
      savedStep: step,
      savedAnswers: answers
    }));
  }, [step, answers, sessionId]);

  // Filter questions based on dwelling type (skip features for flats)
  const questions = ALL_QUESTIONS.filter(q => {
    if (q.skipForFlat && answers.dwelling_type === "flat") return false;
    return true;
  });

  const q = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  // Auto-advance after selection (for non-multi questions) with haptic feedback
  const handleSelect = (val) => {
    setShowTooltip(false);
    feedback.tap(); // Haptic feedback on selection
    
    if (q.multi) {
      // Multi-select doesn't auto-advance
      if (val === "none") {
        setAnswers(p => ({ ...p, features: p.features.includes("none") ? [] : ["none"] }));
      } else {
        setAnswers(p => ({
          ...p,
          features: p.features.includes(val) 
            ? p.features.filter(f => f !== val)
            : [...p.features.filter(f => f !== "none"), val]
        }));
      }
    } else {
      // Single select - auto-advance after selection
      setAnswers(p => ({ ...p, [q.id]: val }));
      
      // Auto-advance after brief delay (150ms for snappy feel)
      setTimeout(() => {
        if (step < questions.length - 1) {
          transitionTo(step + 1);
        } else {
          submit(val);
        }
      }, 150);
    }
  };

  const transitionTo = (nextStep) => {
    setTransitioning(true);
    setShowTooltip(false);
    setTimeout(() => {
      setStep(nextStep);
      setTransitioning(false);
    }, 200);
  };

  const back = () => {
    if (step > 0) transitionTo(step - 1);
  };

  // For multi-select questions, need manual continue
  const continueFromMulti = () => {
    if (step < questions.length - 1) {
      transitionTo(step + 1);
    } else {
      submit();
    }
  };

  const submit = async (lastAnswer) => {
    setLoading(true);
    setExitTransition(true);
    
    // Use lastAnswer for the final question if provided
    const finalAnswers = lastAnswer ? { ...answers, [q.id]: lastAnswer } : answers;
    
    // Prepare diagnostic data synchronously
    let epcBand = "D_E";
    if (finalAnswers.epc_rating === "A-B") epcBand = "A_B";
    else if (finalAnswers.epc_rating === "C") epcBand = "C";
    else if (finalAnswers.epc_rating === "D-E") epcBand = "D_E";
    else if (finalAnswers.epc_rating === "F-G") epcBand = "F_G";
    
    const features = finalAnswers.features.filter(f => f !== "none");
    
    const mapToYesNoUnsure = (value) => {
      if (value === "true" || value === true) return "yes";
      if (value === "false" || value === false) return "no";
      if (value === "partial") return "partial";
      if (value === "unsure") return "unsure";
      return "unsure";
    };
    
    const mapBedrooms = (value) => {
      const num = parseInt(value) || 2;
      if (num >= 4) return "4_plus";
      return String(num);
    };
    
    const storedSession = sessionStorage.getItem(`cipher_session_${sessionId}`);
    const postcode = storedSession ? JSON.parse(storedSession).postcode : "";
    
    const diagnosticData = {
      postcode: postcode,
      property_era: finalAnswers.property_era || "classic",
      dwelling_type: finalAnswers.dwelling_type,
      bedrooms: mapBedrooms(finalAnswers.bedrooms),
      bathrooms: parseInt(finalAnswers.bathrooms) || 1,
      separate_kitchen: mapToYesNoUnsure(finalAnswers.separate_kitchen),
      wide_hallways_over_1m: "unsure",
      layout_changed_since_built: mapToYesNoUnsure(finalAnswers.layout_changed),
      features: features,
      epc_band: epcBand,
      main_goal: finalAnswers.main_goal
    };
    
    // Store diagnostic data for HiPPO email capture
    sessionStorage.setItem(`hippo_diagnostic_${sessionId}`, JSON.stringify(diagnosticData));
    sessionStorage.setItem(`cipher_era_${sessionId}`, finalAnswers.property_era);
    
    // Clear progress after successful preparation
    sessionStorage.removeItem(`cipher_diagnostic_progress_${sessionId}`);
    
    const isPartner = sessionStorage.getItem(`cipher_partner_${sessionId}`) === "true";
    
    // Store diagnostic data in a format the partner context page can read
    if (isPartner) {
      sessionStorage.setItem(`diagnostic_data_${sessionId}`, JSON.stringify(diagnosticData));
    }
    
    // Navigate immediately (200ms for exit transition)
    setTimeout(() => {
      if (isPartner) {
        navigate(`/partner/context/${sessionId}`);
      } else {
        navigate(`/hippo/email/${sessionId}`, { state: { diagnosticData } });
      }
    }, 200);
    
    // Fire API call in background (non-blocking)
    axios.post(`${API}/cipher/diagnostic`, {
      session_id: sessionId,
      property_type: finalAnswers.property_era,
      dwelling_type: finalAnswers.dwelling_type,
      bedrooms: parseInt(finalAnswers.bedrooms) || 1,
      bathrooms: parseInt(finalAnswers.bathrooms) || 1,
      separate_kitchen: finalAnswers.separate_kitchen === "true",
      wide_hallways: false,
      layout_changed: finalAnswers.layout_changed === "true",
      features: features,
      epc_rating: finalAnswers.epc_rating || "unknown",
      main_goal: finalAnswers.main_goal
    }).catch(e => console.error("Background diagnostic save failed:", e));
  };

  // Question rendering with dark UI and micro-interactions
  const renderOption = (opt, idx) => {
    const [val, label] = opt;
    const isSelected = q.multi ? answers.features.includes(val) : answers[q.id] === val;
    const helperText = q.helperText?.[val];
    
    if (q.multi) {
      return (
        <label
          key={val}
          className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${
            isSelected 
              ? "border-cipher-green bg-cipher-green/10 dark:bg-cipher-green/20 animate-scale-bounce" 
              : "border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 hover:border-cipher-green/50 hover:bg-white/10"
          }`}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => handleSelect(val)}
            className="border-2 border-white/30 data-[state=checked]:bg-cipher-green data-[state=checked]:border-cipher-green"
          />
          <span className={`font-medium ${isSelected ? "text-cipher-green" : "text-white/80"}`}>{label}</span>
        </label>
      );
    }
    
    return (
      <button
        key={val}
        onClick={() => handleSelect(val)}
        className={`w-full p-4 rounded-xl border text-left transition-all active:scale-[0.98] ${
          isSelected 
            ? "border-cipher-green bg-cipher-green/10 dark:bg-cipher-green/20 animate-scale-bounce" 
            : "border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 hover:border-cipher-green/50 hover:bg-white/10"
        }`}
        data-testid={`option-${val}`}
      >
        <div className="flex items-center justify-between">
          <span className={`font-medium ${isSelected ? "text-cipher-green" : "text-white/80"}`}>{label}</span>
          {isSelected && <Check className="w-5 h-5 text-cipher-green" />}
        </div>
        {helperText && (
          <p className="mt-2 text-xs text-white/50 leading-relaxed">{helperText}</p>
        )}
      </button>
    );
  };

  return (
    <div className={`fixed inset-0 flex flex-col overflow-hidden transition-opacity duration-300 ${exitTransition ? 'opacity-0' : 'opacity-100'}`}>
      {/* Background with frosted glass effect */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 backdrop-blur-md bg-black/60" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header with progress */}
        <header className="p-4 pb-2">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={back} 
                disabled={step === 0}
                className={`p-2 rounded-lg transition-colors ${
                  step === 0 
                    ? 'opacity-30 cursor-not-allowed' 
                    : 'hover:bg-white/10 text-white'
                }`}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <span className="text-sm text-white/60">{step + 1} of {questions.length}</span>
              <div className="w-9" />
            </div>
            
            {/* Progress bar with glow effect */}
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-cipher-green transition-all duration-500 ease-out rounded-full animate-progress-glow"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </header>

        {/* Question content */}
        <main className="flex-1 overflow-auto p-4 pt-8">
          <div className="max-w-md mx-auto">
            <div className={`transition-all duration-200 ${transitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
              {/* Question card - Glassmorphism */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-2xl">
                {/* Question header with tooltip */}
                <div className="flex items-start justify-between mb-2">
                  <h2 className="text-2xl font-bold text-white">{q?.title}</h2>
                  {q?.tooltip && (
                    <button 
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="p-1 text-white/40 hover:text-cipher-gold transition-colors"
                      aria-label="Show help"
                    >
                      <HelpCircle className="w-5 h-5" />
                    </button>
                  )}
                </div>
                
                <p className="text-white/60 mb-4">{q?.desc}</p>
                
                {/* Tooltip */}
                {showTooltip && q?.tooltip && (
                  <div className="mb-6 p-4 bg-cipher-gold/10 border border-cipher-gold/30 rounded-xl relative animate-fade-up">
                    <button 
                      onClick={() => setShowTooltip(false)}
                      className="absolute top-2 right-2 p-1 text-white/40 hover:text-white/60"
                      aria-label="Close tooltip"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-white/80 pr-6">{q.tooltip}</p>
                  </div>
                )}
                
                <div className="space-y-3">
                  {q?.opts.map((opt, idx) => renderOption(opt, idx))}
                </div>
                
                {/* Continue button only for multi-select */}
                {q?.multi && (
                  <button
                    onClick={continueFromMulti}
                    className="w-full mt-6 p-4 rounded-xl bg-cipher-green text-white font-medium hover:bg-cipher-green/90 shadow-lg shadow-cipher-green/30 transition-all hover:scale-[1.01]"
                    data-testid="continue-btn"
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
        
        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-xs text-white/30">HiPPO by StudioNPG</p>
        </footer>
      </div>
    </div>
  );
}
